// Pure formatting helpers for the monthly report (shared with the native app).
import type { Locale } from './i18n';
import type { AgingBucketKey, SubscriptionMonthStatus } from './monthlyReportApi';

const INTL_LOCALE: Record<Locale, string> = { ro: 'ro-RO', en: 'en-GB' };

/** `12.345,67 RON` — two decimals, the currency spelled out (never a symbol). */
export function fmtMoney(amount: number, currency = 'RON', locale: Locale = 'ro'): string {
  return `${amount.toLocaleString(INTL_LOCALE[locale], {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

/** Signed variant for deltas: `+1.234,00 RON` / `-1.234,00 RON`. */
export function fmtSignedMoney(amount: number, currency = 'RON', locale: Locale = 'ro'): string {
  const sign = amount > 0 ? '+' : amount < 0 ? '-' : '';
  return `${sign}${fmtMoney(Math.abs(amount), currency, locale)}`;
}

export function fmtPct(value: number): string {
  return `${Math.round(value * 10) / 10}%`;
}

export function fmtDate(iso: string | null, locale: Locale = 'ro'): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], { dateStyle: 'medium' }).format(
    new Date(iso),
  );
}

/** "martie 2026" / "March 2026". */
export function monthTitle(year: number, month: number, locale: Locale = 'ro'): string {
  const label = new Intl.DateTimeFormat(INTL_LOCALE[locale], {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, 1)));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Month names for a picker, index 0 = January. */
export function monthNames(locale: Locale = 'ro'): string[] {
  const fmt = new Intl.DateTimeFormat(INTL_LOCALE[locale], { month: 'long', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, i) => {
    const name = fmt.format(new Date(Date.UTC(2026, i, 1)));
    return name.charAt(0).toUpperCase() + name.slice(1);
  });
}

/** Short `mar. 26` style label for the 12-month charts. */
export function shortPeriod(period: string, locale: Locale = 'ro'): string {
  const [y, m] = period.split('-').map(Number);
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, 1)));
}

export function shiftMonth(
  year: number,
  month: number,
  by: number,
): { year: number; month: number } {
  const idx = year * 12 + (month - 1) + by;
  return { year: Math.floor(idx / 12), month: (idx % 12) + 1 };
}

/** Default month for the page: the previous calendar month (a month is reported once it ends). */
export function defaultReportMonth(now = new Date()): { year: number; month: number } {
  return shiftMonth(now.getFullYear(), now.getMonth() + 1, -1);
}

export const AGING_BUCKET_ORDER: AgingBucketKey[] = [
  'current',
  'd1_30',
  'd31_60',
  'd61_90',
  'd90plus',
];

/** Tailwind text colour for a subscription status pill. */
export const SUBSCRIPTION_STATUS_CLASS: Record<SubscriptionMonthStatus, string> = {
  paid: 'bg-green-50 text-green-700 border-green-200',
  missing: 'bg-red-50 text-red-700 border-red-200',
  amount_differs: 'bg-amber-50 text-amber-700 border-amber-200',
  not_due: 'bg-gray-50 text-gray-600 border-gray-200',
};

/** Chart series colours (validated categorical palette: blue / orange / purple). */
export const REPORT_CHART_COLORS = {
  inflows: '#2563eb',
  outflows: '#ea580c',
  net: '#7c3aed',
} as const;
